'use client';

import Icon from '@/components/ui/AppIcon';
import DemoDataPill from './DemoDataPill';
import type {
  QBReportResponse,
  QBSalesByCustomerReport,
  QBSalesByProductReport,
  IntuitReportResponse,
  IntuitReportRow,
} from '@/lib/quickbooks/types';

interface ReportState<T> {
  data: QBReportResponse<T> | null;
  loading: boolean;
  error: string | null;
}

interface Props {
  topCustomers: ReportState<QBSalesByCustomerReport>;
  topProducts:  ReportState<QBSalesByProductReport>;
  onRetryCustomers: () => void;
  onRetryProducts:  () => void;
}

interface RankedEntry {
  label: string;
  value: number;
}

function parseDataRows(report: IntuitReportResponse | undefined): RankedEntry[] {
  if (!report?.Rows?.Row) return [];
  const out: RankedEntry[] = [];
  const walk = (rows: IntuitReportRow[]) => {
    for (const row of rows) {
      if (row.type === 'Data' && row.ColData) {
        const label = row.ColData[0]?.value ?? '';
        const value = parseFloat(row.ColData[1]?.value ?? '0') || 0;
        if (label && value > 0) out.push({ label, value });
      }
      if (row.Rows?.Row) walk(row.Rows.Row);
    }
  };
  walk(report.Rows.Row);
  return out;
}

function formatMoney(val: number): string {
  if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(2)}M`;
  if (val >= 1_000)     return `$${(val / 1_000).toFixed(0)}k`;
  return `$${val.toFixed(0)}`;
}

function staleLabel(fetchedAt: string): string {
  const mins = Math.floor((Date.now() - new Date(fetchedAt).getTime()) / 60_000);
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h`;
}

const CONCENTRATION_THRESHOLD_PCT = 25;

// ── Main ──────────────────────────────────────────────────────────────────

export default function Row6TopPerformers({ topCustomers, topProducts, onRetryCustomers, onRetryProducts }: Props) {
  return (
    <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <TopCustomersCard state={topCustomers} onRetry={onRetryCustomers} />
      <TopProductsCard state={topProducts} onRetry={onRetryProducts} />
    </section>
  );
}

// ── 6A. Top Customers ─────────────────────────────────────────────────────

function TopCustomersCard({ state, onRetry }: { state: ReportState<QBSalesByCustomerReport>; onRetry: () => void }) {
  const isLoading = state.loading && !state.data;
  const isError = !state.loading && state.error && !state.data;
  const entries = parseDataRows(state.data?.data).sort((a, b) => b.value - a.value).slice(0, 10);
  const total = entries.reduce((s, e) => s + e.value, 0);
  const topPct = total > 0 && entries.length > 0 ? (entries[0].value / total) * 100 : 0;
  const concentrationRisk = topPct > CONCENTRATION_THRESHOLD_PCT;
  const mock = !!state.data?.mock;
  const stale = !!state.data?.staleOnError;

  return (
    <div className="relative rounded-xl p-5" style={{ background: '#FFFFFF', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #F0F0F0' }}>
      {mock && <DemoDataPill />}
      <div className="flex items-center justify-between gap-2 mb-3 pr-16">
        <div className="flex items-center gap-2">
          <Icon name="UsersIcon" size={15} style={{ color: '#033494' }} />
          <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
            Top Customers by Revenue
          </h3>
        </div>
        <p className="text-[11px] text-gray-500 font-semibold">YTD</p>
      </div>
      {concentrationRisk && (
        <div
          className="rounded-lg px-2.5 py-1.5 mb-2 text-[10px] font-bold uppercase tracking-wider"
          style={{ background: 'rgba(239,68,68,0.10)', color: '#B91C1C', border: '1px solid rgba(239,68,68,0.25)' }}
        >
          Concentration risk · top customer is {topPct.toFixed(1)}% of revenue
        </div>
      )}
      {stale && state.data && (
        <p className="text-[10px] italic text-gray-400 mb-2">
          Data may be stale · refreshed {staleLabel(state.data.fetchedAt)} ago
        </p>
      )}

      {isLoading ? (
        <LoadingBody />
      ) : isError ? (
        <ErrorBody error={state.error!} onRetry={onRetry} tag="[Customers]" />
      ) : entries.length === 0 ? (
        <EmptyBody message="No customer revenue data for YTD" />
      ) : (
        <RankedBars entries={entries} total={total} barColor="#07B0E9" />
      )}
    </div>
  );
}

// ── 6B. Top Products ──────────────────────────────────────────────────────

function TopProductsCard({ state, onRetry }: { state: ReportState<QBSalesByProductReport>; onRetry: () => void }) {
  const isLoading = state.loading && !state.data;
  const isError = !state.loading && state.error && !state.data;
  const entries = parseDataRows(state.data?.data).sort((a, b) => b.value - a.value).slice(0, 10);
  const total = entries.reduce((s, e) => s + e.value, 0);
  const mock = !!state.data?.mock;
  const stale = !!state.data?.staleOnError;

  return (
    <div className="relative rounded-xl p-5" style={{ background: '#FFFFFF', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #F0F0F0' }}>
      {mock && <DemoDataPill />}
      <div className="flex items-center justify-between gap-2 mb-3 pr-16">
        <div className="flex items-center gap-2">
          <Icon name="CubeIcon" size={15} style={{ color: '#10B981' }} />
          <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
            Top Products / Services
          </h3>
        </div>
        <p className="text-[11px] text-gray-500 font-semibold">YTD</p>
      </div>
      {stale && state.data && (
        <p className="text-[10px] italic text-gray-400 mb-2">
          Data may be stale · refreshed {staleLabel(state.data.fetchedAt)} ago
        </p>
      )}

      {isLoading ? (
        <LoadingBody />
      ) : isError ? (
        <ErrorBody error={state.error!} onRetry={onRetry} tag="[Products]" />
      ) : entries.length === 0 ? (
        <EmptyBody message="No product revenue data for YTD" />
      ) : (
        <RankedBars entries={entries} total={total} barColor="#10B981" />
      )}
    </div>
  );
}

// ── Shared ranked bar list ────────────────────────────────────────────────

function RankedBars({ entries, total, barColor }: { entries: RankedEntry[]; total: number; barColor: string }) {
  const maxVal = entries[0]?.value ?? 1;
  return (
    <div className="space-y-2">
      {entries.map((e, i) => {
        const pct = total > 0 ? (e.value / total) * 100 : 0;
        const barWidth = (e.value / maxVal) * 100;
        return (
          <div key={`${e.label}-${i}`}>
            <div className="flex items-center justify-between text-[11px] mb-0.5">
              <span className="font-semibold text-gray-700 truncate">
                <span className="text-gray-400 mr-1.5">#{i + 1}</span>
                {e.label}
              </span>
              <span className="text-gray-700 font-mono flex-shrink-0 ml-2">
                {formatMoney(e.value)} <span className="text-gray-400">· {pct.toFixed(1)}%</span>
              </span>
            </div>
            <div className="h-1.5 rounded-full" style={{ background: '#F3F4F6' }}>
              <div className="h-1.5 rounded-full" style={{ width: `${barWidth}%`, background: barColor }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function LoadingBody() {
  return (
    <div className="py-8 flex items-center justify-center">
      <div className="w-5 h-5 rounded-full border-[2.5px] border-[#07B0E9] border-t-transparent animate-spin" />
    </div>
  );
}

function ErrorBody({ error, onRetry, tag }: { error: string; onRetry: () => void; tag: string }) {
  return (
    <div className="rounded-lg p-3 flex items-start gap-2" style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.18)' }}>
      <Icon name="ExclamationTriangleIcon" size={16} style={{ color: '#B91C1C', flexShrink: 0 }} />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold" style={{ color: '#7F1D1D' }}>{tag} fetch failed</p>
        <p className="text-[11px] text-gray-600 mt-0.5 break-words">{error}</p>
        <button
          onClick={onRetry}
          className="mt-2 rounded-md px-2.5 py-1 text-[11px] font-semibold text-white hover:opacity-90 active:scale-95 transition"
          style={{ background: '#EF4444' }}
        >
          Retry
        </button>
      </div>
    </div>
  );
}

function EmptyBody({ message }: { message: string }) {
  return (
    <div className="rounded-lg p-3 flex items-center gap-2" style={{ background: 'rgba(107,114,128,0.06)', border: '1px solid rgba(107,114,128,0.18)' }}>
      <Icon name="DocumentTextIcon" size={16} style={{ color: '#6B7280' }} />
      <p className="text-xs font-medium text-gray-600">{message}</p>
    </div>
  );
}
